/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { JobOrderDelivery, JobOrderMethod } from '@gh/builder-shared-data';
import {
  CELL_TEXT_RIGHT_CLASS,
  conditionalCellRenderer,
  createDefaultGridModel,
  dictionaryValueFormatter,
  rendererIconByName,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { DateTimeTypes, getRouteInitData, NumberTypes, State } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { of$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';
import { isEmpty, isNil, negate } from 'lodash';
import { Observable } from 'rxjs';
import {
  getJeOrderId,
  isJeOrder0,
  isJeOrderProduced,
  JeOrder,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  queryJeCostCenterOrders,
  queryJeEditable,
  queryJeOrderItems,
  queryJeSelectedCostCenterId,
} from '../je-details.state';

@Component({
  standalone: false,
  selector: 'gh-je-details-order-list',
  templateUrl: 'je-details-order-list.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JeDetailsOrderListComponent implements OnInit {
  orders$: Observable<JeOrder[]>;
  gridDefinition: LayoutBasedGridDefinition;
  editable$: Observable<boolean>;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private state: State<JeDetailsState>,
              private commands: JeCommandService,
              private translations: TranslationService) {

  }

  ngOnInit(): void {
    const { orderGridLayout, sendOrderByDict, orderDeliveryByDict } = getRouteInitData(this.route);
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model: createDefaultGridModel({
        editable: this.state.select(queryJeEditable),
        getId: getJeOrderId,
      }),
      layout: orderGridLayout,
      options: {
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: JeOrder }) => !isNil(data.dirtyType),
        },
      },
      columns: {
        orderNumber: { cellClass: CELL_TEXT_RIGHT_CLASS },
        deliveryDate: {
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
          valueGetter({ data }: { data: JeOrder }): Maybe<Date> {
            return data.delivery === JobOrderDelivery.Entered ? data.deliveryDate : data.deliveryScheduleDate;
          },
        },
        amount: buildNumberColumn(NumberTypes.SignedPrice),
        attachments: { cellRenderer: conditionalCellRenderer(negate(isEmpty), rendererIconByName('attach-file')) },
        delivery: { valueFormatter: dictionaryValueFormatter(orderDeliveryByDict) },
        status: {
          field: 'method',
          cellClassRules: {
            'cell--positive': ({ data }: { data: JeOrder }) => isJeOrderProduced(data),
          },
          valueFormatter: ({ data }: { data: JeOrder }) =>
            isJeOrderProduced(data) ? ({
              [JobOrderMethod.Email]: this.translations.get('lbl_emailed'),
              [JobOrderMethod.Printer]: this.translations.get('lbl_printed'),
            }[data.producedMethod!]) : sendOrderByDict.get(data.method),
        },
      },
    });

    this.editable$ = this.state.select(queryJeEditable);
    this.orders$ = this.state.select(queryJeSelectedCostCenterId).pipe(
      switchMap$((costCenterId) => this.state.select(queryJeCostCenterOrders, costCenterId)));
  }

  onAdd(): void {
    const ccId = this.state.project(queryJeSelectedCostCenterId);
    if (ccId) {
      this.commands.orderAdd(ccId);
    }
  }

  onOpen(order: JeOrder): void {
    this.commands.orderOpen(getJeOrderId(order));
  }

  onDelete(order: JeOrder): void {
    this.commands.orderRemove(getJeOrderId(order));
  }

  canBeDeleted$(order: JeOrder): Observable<boolean> {
    // do not allow to remove Order 0
    return isJeOrder0(order) ? of$(false) : this.state.select(queryJeOrderItems, getJeOrderId(order))
      .pipe(map$(isEmpty));
  }
}
